import React, { FC, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Cart, LineItem } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { CloudinaryProductAssets, ContentfulProduct } from 'shared/types/content';
import { Variant } from 'shared/types/product';
import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import {
  CART_CUSTOM_LINE_ITEM_ID_QUERY_PARAM,
  CART_LINE_ITEM_ID_QUERY_PARAM,
} from 'helpers/constants/query-params';
import usePath from 'helpers/hooks/usePath';
import { dedupeCloudinaryImages } from 'helpers/mappers/cloudinaryAssetToImage';
import { getProductGalleryImages } from 'helpers/mappers/getProductGalleryImages';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUIProductContent } from 'helpers/mappers/toUIProductContent';
import ProductDetails, { ProductDetailsProps } from '..';
import { getProductParentCategory } from './getProductParentCategory';
import { getImageCacheKey } from './imageCacheKey';
import { getProductDescription } from './productDescription';
import { usePrefetchLeadAxisImages } from '../hooks/usePrefetchLeadAxisImages';
import { useProductBaseImages } from '../hooks/useProductBaseImages';

type ProductDetailsAdapterProps = {
  assets?: CloudinaryProductAssets;
  cart?: Cart;
  content?: ContentfulProduct;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
  ) => Promise<Cart>;
  origin?: string;
  feedLifestyleImage?: string;
  product: ExtendedProductDetailProduct;
  removeLineItem?: (item: LineItem & { customLineItemId?: string }) => Promise<void>;
  updateLineItem?: (
    lineItemId: string,
    customLineItemId: string | undefined,
    quantity: number,
  ) => Promise<{ isError: boolean; data?: Cart; error?: unknown }>;
  reviewListProps?: ProductDetailsProps['reviewListProps'];
  setIsOpen?: (value: boolean) => void;
  wirebrushModal?: ProductDetailsProps['wirebrushModal'];
  woodFinishHelper?: ProductDetailsProps['woodFinishHelper'];
  phoneLayout?: ProductDetailsProps['phoneLayout'];
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  content,
  assets,
  woodFinishHelper,
  wirebrushModal,
  phoneLayout,
  inModalVersion,
  cart,
  setIsOpen,
  onAddToCart,
  removeLineItem,
  updateLineItem,
  reviewListProps,
  origin,
  feedLifestyleImage,
}) => {
  const { path } = usePath();
  const searchParams = useSearchParams();
  const category = getProductParentCategory(product);

  const [variant, setVariant] = useState(() => {
    if (inModalVersion) {
      return product?.variants[0];
    } else {
      const currentVariantPath = path.split('/');

      // access position 3 since variantPath = ['', 'slug', 'p', 'sku', '']
      const currentVariantSKU = currentVariantPath[3]?.split('?')[0];

      const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);

      return product.variants[currentVariantIndex] ?? product.variants?.[0];
    }
  });

  const inCartQuantity = cart?.lineItems?.find((lineItem) => lineItem.variant?.sku === variant.sku)?.count ?? 0;
  const editLineItemId = searchParams.get(CART_LINE_ITEM_ID_QUERY_PARAM);
  const editLineItem = cart?.lineItems?.find((lineItem: ExtendedLineItem) => {
    if (!editLineItemId || lineItem.lineItemId !== editLineItemId) return false;

    const customLineItemId = searchParams.get(CART_CUSTOM_LINE_ITEM_ID_QUERY_PARAM);
    return customLineItemId ? lineItem.customLineItemId === customLineItemId : !lineItem.customLineItemId;
  });
  const isCartItemEditPending = !!editLineItemId && !editLineItem;

  const mappedProduct = toUIProduct(product, variant);
  const mappedContent = toUIProductContent(content, assets?.shared);
  // Cache the image by the attributes that can actually change it, while fetching with the current
  // SKU: a difference the camera cannot see (system angle, dimensions) is a cache hit, and every
  // difference it can see refetches. Seeded by the server payload for the initial SKU.
  const imageKey = getImageCacheKey(variant);
  const baseImageGroup = useProductBaseImages(product.productId, imageKey, variant.sku, assets?.bySku?.[variant.sku]);
  // Warm the other materials/species in the background so switching the lead axis is instant.
  usePrefetchLeadAxisImages(product.productId, product.variants, variant);
  const galleryImages = getProductGalleryImages(baseImageGroup, assets?.shared, variant.sku, variant.images);

  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);

    if (variantsToUse) {
      setVariant(variantsToUse);
      if (!inModalVersion) {
        const currentPath = window.location.pathname.split('/');
        currentPath[3] = sku; // replace SKU segment in '/slug/p/[sku]'
        const newPath = currentPath.join('/') + window.location.search;
        // The tab title is product-level (the server's meta title), so it stays put on a switch.
        window.history.replaceState({ ...window.history.state, as: newPath, url: newPath }, '', newPath);
      }
    }
  };

  // Both derived together so the visible copy and the JSON-LD copy cannot drift apart.
  const { node: description, plainText: plainTextDescription } = getProductDescription(
    mappedContent?.description,
    product.description,
  );

  return (
    <ProductDetails
      product={{
        ...mappedProduct,
        feedLifestyleImage,
        images: galleryImages,
        lifestyleImages: dedupeCloudinaryImages(assets?.lifestyle ?? []),
      }}
      phoneLayout={phoneLayout}
      woodFinishHelper={woodFinishHelper}
      wirebrushModal={wirebrushModal}
      productContent={mappedContent}
      description={description}
      plainTextDescription={plainTextDescription}
      variant={variant}
      category={category}
      url={product._url}
      inModalVersion={inModalVersion}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
      removeLineItem={removeLineItem}
      updateLineItem={updateLineItem}
      editLineItem={editLineItem}
      isCartItemEditPending={isCartItemEditPending}
      inCartQuantity={inCartQuantity}
      reviewListProps={reviewListProps}
      origin={origin}
    />
  );
};

export default ProductDetailsAdapter;
